import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, Check, ShoppingCart, Package, Shield, Leaf, Award, X, Clock } from 'lucide-react';
import { productDetails } from '@/data/productDetails';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAffiliateLink } from '@/hooks/useAffiliateLink';
import { createConversionClickHandler } from '@/utils/conversionTracking';
import { MobileFloatingCTA } from '@/components/MobileFloatingCTA';

const CTA_VARIANT_KEY = 'cta_text_variant_v1';
const CTA_VARIANTS = [
	{ key: 'A', text: 'Zum Angebot' },
	{ key: 'B', text: 'Preis prüfen' },
];

const ProductReviewPage = () => {
  const { slug } = useParams();
  const product = productDetails[slug];
  const affiliateLink = useAffiliateLink(product?.affiliateLink);
  const handleClick = createConversionClickHandler(affiliateLink);

	  // Session-level CTA A/B (keeps one variant per session, across pages)
	  const [ctaVariant] = useState(() => {
	    if (typeof window === 'undefined') return CTA_VARIANTS[0];
	    try {
	      const stored = sessionStorage.getItem(CTA_VARIANT_KEY);
	      const found = CTA_VARIANTS.find((v) => v.key === stored);
	      if (found) return found;
	      const pick = Math.random() < 0.5 ? CTA_VARIANTS[0] : CTA_VARIANTS[1];
	      sessionStorage.setItem(CTA_VARIANT_KEY, pick.key);
	      return pick;
	    } catch (_) {
	      return CTA_VARIANTS[0];
	    }
	  });

	  const ctaText = useMemo(() => ctaVariant?.text || 'Zum Angebot', [ctaVariant]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Produkt nicht gefunden</h1>
          <Link to="/" className="text-primary hover:underline">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  const StarRating = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`h-5 w-5 ${i < fullStars ? 'fill-amber-400 text-amber-400' : i === fullStars && hasHalfStar ? 'fill-amber-400/50 text-amber-400' : 'fill-muted text-muted'}`} />
        ))}
        <span className="ml-2 text-lg font-semibold text-foreground">{rating.toFixed(1)}</span>
      </div>
    );
  };

	  return (
	    <div className="min-h-screen bg-background pb-28 md:pb-0">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="h-4 w-4" />Zurück zum Vergleich
          </Link>
        </div>
      </div>

      <div className="bg-gradient-to-b from-primary/5 to-background py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <img src={product.image} alt={product.name} className="w-full h-auto rounded-lg" />
            </div>
            <div>
              {product.badge && (
                <Badge className="mb-4 bg-amber-500 text-white px-4 py-1">
                  <Award className="h-4 w-4 mr-1" />{product.badge}
                </Badge>
              )}
              <h1 className="text-4xl font-bold text-foreground mb-4">{product.name}</h1>
              <div className="mb-4"><StarRating rating={product.rating} /></div>
              <p className="text-xl text-primary font-bold mb-6">{product.price}</p>
	              <p className="text-muted-foreground mb-6 leading-relaxed">{product.description}</p>

	              {(product.decision || product.preparation?.frequency) && (
	                <div className="mb-6 rounded-xl border border-border bg-white/80 p-4 shadow-sm">
	                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
	                    <Clock className="h-4 w-4 text-primary" />30-Sekunden-Check
	                  </div>
	                  {product.decision?.quickTake && (
	                    <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{product.decision.quickTake}</p>
	                  )}
	                  <div className="grid gap-3 sm:grid-cols-2">
	                    {product.decision?.forWho?.length ? (
	                      <div>
	                        <div className="text-xs font-semibold text-foreground mb-1">Geeignet für</div>
	                        <ul className="space-y-1">
	                          {product.decision.forWho.slice(0, 3).map((item, idx) => (
	                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
	                              <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
	                              <span>{item}</span>
	                            </li>
	                          ))}
	                        </ul>
	                      </div>
	                    ) : null}

	                    {product.decision?.notForWho?.length ? (
	                      <div>
	                        <div className="text-xs font-semibold text-foreground mb-1">Weniger geeignet für</div>
	                        <ul className="space-y-1">
	                          {product.decision.notForWho.slice(0, 3).map((item, idx) => (
	                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
	                              <X className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
	                              <span>{item}</span>
	                            </li>
	                          ))}
	                        </ul>
	                      </div>
	                    ) : null}
	                  </div>
	                  {product.preparation?.frequency && (
	                    <p className="mt-3 text-xs text-muted-foreground">
	                      <strong>Empfehlung:</strong> {product.preparation.frequency}
	                    </p>
	                  )}
	                </div>
	              )}
	              <a
	                href={affiliateLink}
	                onClick={handleClick}
	                rel="noopener noreferrer sponsored"
	                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
	              >
	                <ShoppingCart className="h-5 w-5" />{ctaText}
	              </a>
	              <p className="text-xs text-muted-foreground mt-2">Weiterleitung zum Anbieter. Preise/Verfügbarkeit können variieren.</p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {product.properties?.vegan && (<div className="flex items-center gap-2 text-sm"><Leaf className="h-4 w-4 text-primary" /><span>Vegan</span></div>)}
                {product.properties?.glutenFree && (<div className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" /><span>Glutenfrei</span></div>)}
                {product.guarantee && (<div className="flex items-center gap-2 text-sm"><Shield className="h-4 w-4 text-primary" /><span>{product.guarantee.days} Tage Garantie</span></div>)}
                {product.packaging && (<div className="flex items-center gap-2 text-sm"><Package className="h-4 w-4 text-primary" /><span>Premium Teebeutel</span></div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          {product.benefits && (
            <Card>
              <CardHeader><CardTitle>Hauptmerkmale</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {product.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Zutaten</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-6">
                {product.ingredients.map((ingredient, index) => (
                  <Badge key={index} variant="secondary" className="px-3 py-1">{ingredient}</Badge>
                ))}
              </div>
              {product.ingredientDetails && (
                <div className="space-y-4 mt-6">
                  <h3 className="font-semibold text-lg mb-4">Zutatenbeschreibung</h3>
                  {product.ingredientDetails.map((detail, index) => (
                    <div key={index} className="border-l-2 border-primary/20 pl-4">
                      <h4 className="font-semibold text-primary mb-1">{detail.name}</h4>
                      <p className="text-sm text-muted-foreground">{detail.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {product.taste && (
              <Card>
                <CardHeader><CardTitle>Geschmack</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-semibold text-primary mb-3">{product.taste.profile}</p>
                  {product.taste.notes && (
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {product.taste.notes.map((note, index) => (<li key={index}>• {note}</li>))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}
            {product.caffeine && (
              <Card>
                <CardHeader><CardTitle>Koffeingehalt</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-semibold mb-2">{product.caffeine.contains ? 'Enthält Koffein' : 'Koffeinfrei'}</p>
                  {product.caffeine.amount && (<p className="text-sm text-muted-foreground mb-2">Menge: {product.caffeine.amount}</p>)}
                  {product.caffeine.note && (<p className="text-sm text-muted-foreground">{product.caffeine.note}</p>)}
                </CardContent>
              </Card>
            )}
          </div>

          {product.preparation && (
            <Card>
              <CardHeader><CardTitle>Zubereitung</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {product.preparation.hot && (<div><h4 className="font-semibold mb-2">Heiß</h4><p className="text-sm text-muted-foreground">{product.preparation.hot}</p></div>)}
                  {product.preparation.cold && (<div><h4 className="font-semibold mb-2">Kalt</h4><p className="text-sm text-muted-foreground">{product.preparation.cold}</p></div>)}
                  {product.preparation.frequency && (<div><h4 className="font-semibold mb-2">Empfohlene Häufigkeit</h4><p className="text-sm text-muted-foreground">{product.preparation.frequency}</p></div>)}
                  {product.preparation.steepTime && (<div><h4 className="font-semibold mb-2">Ziehzeit</h4><p className="text-sm text-muted-foreground">{product.preparation.steepTime}</p></div>)}
                </div>
              </CardContent>
            </Card>
          )}

          {product.properties && (
            <Card>
              <CardHeader><CardTitle>Eigenschaften</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {product.properties.vegan && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">Vegan</span></div>)}
                  {product.properties.glutenFree && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">Glutenfrei</span></div>)}
                  {product.properties.dairyFree && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">Milchfrei</span></div>)}
                  {product.properties.soyFree && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">Sojafrei</span></div>)}
                  {product.properties.gmoFree && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">Gentechnikfrei</span></div>)}
                  {product.properties.sugarFree && (<div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{product.properties.sugarFree}</span></div>)}
                </div>
              </CardContent>
            </Card>
          )}

          {product.faq && (
            <Card>
              <CardHeader><CardTitle>Häufig gestellte Fragen</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {product.faq.sideEffects && (<div><h4 className="font-semibold mb-2">Nebenwirkungen</h4><p className="text-sm text-muted-foreground">{product.faq.sideEffects}</p></div>)}
                  {product.faq.medications && (<div><h4 className="font-semibold mb-2">Medikamente</h4><p className="text-sm text-muted-foreground">{product.faq.medications}</p></div>)}
                  {product.faq.whenResults && (<div><h4 className="font-semibold mb-2">Wann zeigen sich Ergebnisse?</h4><p className="text-sm text-muted-foreground">{product.faq.whenResults}</p></div>)}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {product.guarantee && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" />Garantie</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-semibold text-primary mb-2">{product.guarantee.days} Tage Geld-zurück-Garantie</p>
                  <p className="text-sm text-muted-foreground">{product.guarantee.description}</p>
                </CardContent>
              </Card>
            )}
            {product.shipping && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-primary" />Versand & Rückgabe</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {product.shipping.processingTime && (<p><strong>Bearbeitung:</strong> {product.shipping.processingTime}</p>)}
                    {product.shipping.deliveryTime && (<p><strong>Lieferzeit:</strong> {product.shipping.deliveryTime}</p>)}
                    {product.shipping.returnPolicy && (<p><strong>Rückgabe:</strong> {product.shipping.returnPolicy.days} Tage</p>)}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

	          <Card className="bg-gradient-to-r from-primary/5 to-amber-500/5 border-primary/20">
            <CardContent className="py-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Jetzt {product.name} bestellen</h3>
              <p className="text-muted-foreground mb-6">Profitieren Sie von unseren exklusiven Angeboten</p>
	              <a
	                href={affiliateLink}
	                onClick={handleClick}
	                rel="noopener noreferrer sponsored"
	                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
	              >
	                <ShoppingCart className="h-5 w-5" />{ctaText}
	              </a>
            </CardContent>
          </Card>

          <Card className="bg-muted/50">
            <CardContent className="py-6">
              <p className="text-xs text-muted-foreground text-center">
                <strong>Hinweis:</strong> Die auf dieser Seite dargestellten Informationen dienen ausschließlich zu Informationszwecken.
                Sie stellen keine medizinische Beratung dar und ersetzen nicht die Konsultation eines Arztes oder Apothekers.
	                Aussagen zum Wohlbefinden beruhen teils auf Herstellerangaben und Erfahrungsberichten und sind nicht als medizinische Heilversprechen zu verstehen.
	                Bei Beschwerden oder Unsicherheit wenden Sie sich bitte an Arzt oder Apotheker.
              </p>
            </CardContent>
          </Card>
	        </div>
	      </div>

	      {/* Mobile Floating CTA (current product) */}
	      <MobileFloatingCTA
	        affiliateLinkTemplate={product?.affiliateLink}
	        productName={product?.name}
	        badge={product?.badge}
	        price={product?.price}
	        ctaText={ctaText}
	      />
	    </div>
  );
};

export default ProductReviewPage;
